import type { ApiClient } from '@kiao-voice/api-client'
import type { MeResponse, UpdateUseCaseRequest, UserUseCase } from '@kiao-voice/api-contract/auth'
import { createWebAuthedNoContextClient, getWebAuthedNoContextClient } from '@/lib/web-api-client'

interface RequestOptions {
  /** Test seam: a stubbed `fetch` for a one-off client. */
  fetchFn?: typeof fetch
}

/**
 * Both calls are about the account, not the workspace being viewed, so they go
 * through the authed client that sends no `X-Active-Context`: a stale stored
 * context must not 403 a question that does not depend on it.
 */
function client(options: RequestOptions): ApiClient {
  return options.fetchFn
    ? createWebAuthedNoContextClient(options.fetchFn)
    : getWebAuthedNoContextClient()
}

/**
 * Whether the「主要用途」question is still open for the signed-in user.
 *
 * Only an explicit `useCaseAnswered: false` opens it. An API that predates the
 * field omits it, and that reads as answered: the question is a nudge, so the
 * safe default is to not show it.
 */
export async function fetchUseCaseQuestionOpen(options: RequestOptions = {}): Promise<boolean> {
  const { user } = await client(options).request<MeResponse>('/api/v1/auth/me', { method: 'GET' })
  return user.useCaseAnswered === false
}

/** Record the answer; `null` records a skip. The API answers 204. */
export async function saveUseCase(
  useCase: UserUseCase | null,
  options: RequestOptions = {}
): Promise<void> {
  const body: UpdateUseCaseRequest = { useCase }
  await client(options).request<void>('/api/v1/auth/use-case', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}
